import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { BackIcon, ContactsIcon } from '@/lib/icon';
import { Button, Tooltip } from '@/lib/v2/components';

import { EmblueLegacyContext } from '@/src/App';
import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { BackToEmblueTooltip } from '@/src/presentation/components/BackToEmblueTooltip/BackToEmblueTooltip';

// TODO: Create components
// TODO: Replace div with Stack components

type ContactButtonProps = {
  name: string;
  to: string;
  current: boolean;
  enable: boolean;
};
export const ContactsMenuBar = () => {
  const { t } = useTranslation();
  const { setContext, ...emblueLegacyContext } = useContext(EmblueLegacyContext);
  const location = useLocation();

  const isMenuImportEnabled = useFeatureFlag('contactMenuBarImport');

  const contactButtons = useMemo(
    () => [
      {
        name: t('CONTACTS_MENU_BAR.all'),
        to: '/v2/contacts',
        current: location.pathname === '/v2/contacts',
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.groups'),
        to: '/v2/contacts/groups',
        current: location.pathname.includes('/v2/contacts/groups'),
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.segments'),
        to: '/v2/contacts/segments',
        current: location.pathname.includes('/v2/contacts/segments'),
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.tags'),
        to: '/v2/contacts/tags',
        current: location.pathname.includes('/v2/contacts/tags'),
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.items'),
        to: '/v2/contacts/items',
        current: location.pathname.includes('/v2/contacts/items'),
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.discarded'),
        to: '/v2/contacts/discarded',
        current: location.pathname.includes('/v2/contacts/discarded'),
        enable: true,
      },
      {
        name: t('CONTACTS_MENU_BAR.import'),
        to: '/v2/contacts/import',
        current: location.pathname.includes('/v2/contacts/import'),
        enable: isMenuImportEnabled,
      },
      {
        name: t('CONTACTS_MENU_BAR.happiness'),
        to: '/v2/contacts/happiness',
        current: location.pathname.includes('/v2/contacts/happiness'),
        enable: true,
      },
    ],
    [t, location.pathname, isMenuImportEnabled]
  ); //TODO: CLEAN ENABLE PROP WHEN isMenuImportEnabled FF IS REMOVED

  const [value, setValue] = useState<ContactButtonProps[]>(contactButtons);

  const clickHandler = useCallback(
    (item: ContactButtonProps) => {
      let newCurrent = null;
      return () => {
        const newValue = value.map((element: ContactButtonProps) => {
          newCurrent = element.name === item.name ? true : false;
          return {
            ...element,
            current: newCurrent,
          };
        });
        setValue(newValue);
      };
    },
    [value]
  );

  const classNames = (...classes: string[]) => {
    return classes.filter(Boolean).join(' ');
  };

  useEffect(() => {
    setValue(contactButtons);
  }, [contactButtons, t]);

  return (
    <div className="relative z-10 flex h-[72px] w-full items-center justify-between bg-white px-[5%] shadow-md lg:gap-4 xl:gap-12 2xl:gap-16">
      <div className="flex h-full">
        <div className="flex shrink-0 items-center lg:mr-8 xl:mr-16 2xl:mr-24">
          <ContactsIcon />
        </div>
        <nav className="flex lg:gap-2 xl:gap-10 2xl:gap-16">
          {value.map(
            (item: ContactButtonProps) =>
              item.enable && (
                <NavLink
                  key={item.name}
                  className={classNames(
                    item.current
                      ? 'border-[#004DBC] text-[#004DBC] border-b-[5px] py-6 px-3'
                      : 'border-transparent text-[#364365] hover:border-gray-300',
                    'hover:text-gray-700 inline-flex text-sm py-6 px-3'
                  )}
                  to={item.to}
                  onClick={() => clickHandler(item)}
                >
                  {item.name}
                </NavLink>
              )
          )}
        </nav>
      </div>
      <div className="flex w-full max-w-[230px] items-center whitespace-nowrap text-[14px]">
        <Tooltip content={<BackToEmblueTooltip />}>
          <Button
            outline
            iconLeft={<BackIcon />}
            onClick={() => {
              setContext({ ...emblueLegacyContext, contacts: true });
            }}
          >
            {t('backToLegacy')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
